import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Key, ArrowRight } from 'lucide-react';
import { GoogleLogin, useGoogleOneTapLogin } from '@react-oauth/google';
import { useTenant } from '../context/TenantContext';

import api from '../services/api';
import { useToast } from '../context/ToastContext';

export default function Login() {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [loginMode, setLoginMode] = useState('password'); // 'password' or 'otp'
    const [loading, setLoading] = useState(false);
    const returnOrigin = new URLSearchParams(window.location.search).get('return_origin');
    const [showEmailForm, setShowEmailForm] = useState(!returnOrigin);
    const [restaurantAccounts, setRestaurantAccounts] = useState(null);
    const [googleCredential, setGoogleCredential] = useState(null);
    const { login, googleLogin, setAuth } = useAuth();
    const { isPortal, tenant } = useTenant();
    const navigate = useNavigate();
    const toast = useToast();

    // Automatically trigger Google One Tap if returnOrigin is present
    useGoogleOneTapLogin({
        onSuccess: (credentialResponse) => {
            handleGoogleSuccess(credentialResponse.credential);
        },
        onError: () => {
            console.log('Google One Tap prompt closed or skipped');
        },
        disabled: !returnOrigin
    });

    // Handle "Magic Login" (impersonation) from Super Admin Switch
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const magicToken = urlParams.get('token');
        const redirectPath = urlParams.get('redirect') || '/';
        console.log('[LOGIN_MAGIC_TOKEN] Detected magic token URL search:', window.location.search);
        console.log('[LOGIN_MAGIC_TOKEN] Resolved redirectPath:', redirectPath);
        if (magicToken) {
            localStorage.setItem('token', magicToken);
            toast.success('Login successful. Welcome!');
            window.location.href = redirectPath;
        }
    }, []);

    const getBaseHost = () => {
        const hostname = window.location.hostname;
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            return window.location.host;
        }
        if (hostname.endsWith('aaharamcloud.com')) {
            return 'aaharamcloud.com';
        }
        if (hostname.endsWith('aaharamcloud.vercel.app')) {
            return 'aaharamcloud.vercel.app';
        }
        if (hostname.endsWith('savorybites.vercel.app')) {
            return 'savorybites.vercel.app';
        }
        const parts = hostname.split('.');
        if (parts.length > 2) {
            return parts.slice(-2).join('.');
        }
        return window.location.host;
    };

    const navigateBasedOnRole = (user, token) => {
        const urlParams = new URLSearchParams(window.location.search);
        const redirectQuery = urlParams.get('redirect');
        const branchQuery = urlParams.get('branch');
        console.log('[LOGIN] navigateBasedOnRole params:', { redirectQuery, branchQuery, userRole: user.role });

        if (branchQuery) {
            localStorage.setItem('active_branch_slug', branchQuery);
        }

        if (user.role === 'franchise_admin' && user.franchise_slug) {
            if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                window.location.href = `${window.location.origin}/?restaurant=${user.franchise_slug}`;
            } else {
                const baseHost = getBaseHost();
                const protocol = window.location.protocol;
                window.location.href = `${protocol}//${user.franchise_slug}.${baseHost}/`;
            }
            return;
        }

        const targetSlug = branchQuery || user.slug || localStorage.getItem('active_branch_slug');
        const customUrl = user.custom_frontend_url;

        if (user.role !== 'super_admin' && (targetSlug || customUrl)) {
            const baseHost = getBaseHost();
            const protocol = window.location.protocol;
            
            let domain = '';
            if (customUrl) {
                domain = customUrl.replace(/\/$/, '');
            } else if (targetSlug && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
                domain = `${protocol}//${targetSlug}.${baseHost}`;
            }

            let path = redirectQuery || '/';
            if (!redirectQuery) {
                if (user.role === 'restaurant_admin') path = '/admin';
                else if (user.role === 'kitchen_staff') path = '/kitchen';
                else if (user.role === 'delivery_partner') path = '/delivery';
                else if (user.role === 'customer' || !user.role) path = '/orders';
            }

            if (domain && domain !== window.location.origin) {
                window.location.href = `${domain}/login?token=${token}&redirect=${path}`;
                return;
            }

            // On localhost, subdomains aren't available, so use ?restaurant= query param
            // to let TenantContext resolve the correct tenant
            if (!domain && targetSlug && ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
                localStorage.setItem('tenant_slug', targetSlug);
                const separator = path.includes('?') ? '&' : '?';
                window.location.href = `${window.location.origin}${path}${separator}restaurant=${targetSlug}`;
                return;
            }
        }

        if (user.role === 'super_admin') {
            navigate('/super-admin');
        } else if (user.role === 'restaurant_admin') {
            navigate(redirectQuery || '/admin');
        } else if (user.role === 'kitchen_staff') {
            navigate(redirectQuery || '/kitchen');
        } else if (user.role === 'delivery_partner') {
            navigate(redirectQuery || '/delivery');
        } else {
            navigate(redirectQuery || '/orders');
        }
    };

    const handleAccountSelect = async (restaurantId, userId) => {
        setLoading(true);
        try {
            if (googleCredential) {
                const res = await googleLogin(googleCredential, null, restaurantId, userId);
                const urlParams = new URLSearchParams(window.location.search);
                const returnOrigin = urlParams.get('return_origin');
                
                if (returnOrigin) {
                    window.location.href = `${returnOrigin}/login?token=${res.token}`;
                    return;
                }
                
                toast.success('Signed in with Google');
                navigateBasedOnRole(res.user, res.token);
                setGoogleCredential(null);
                setRestaurantAccounts(null);
            } else {
                const res = await login(formData.email, formData.password, restaurantId);
                if (res.requiresVerification) {
                    toast.info('Verification required');
                    navigate(`/verify-otp${window.location.search}`, { state: { email: formData.email, type: 'signup' } });
                } else {
                    navigateBasedOnRole(res.user, res.token);
                }
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Login failed');
            setRestaurantAccounts(null);
            setGoogleCredential(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (loginMode === 'password') {
                const res = await login(formData.email, formData.password);
                
                if (res.requireRestaurantSelection) {
                    setRestaurantAccounts(res.accounts);
                    return;
                }

                if (res.requiresVerification) {
                    toast.info('Verification required');
                    navigate(`/verify-otp${window.location.search}`, { state: { email: formData.email, type: 'signup' } });
                } else {
                    navigateBasedOnRole(res.user, res.token);
                }
            } else {
                const response = await api.post('/api/auth/request-otp', { email: formData.email, type: 'login' });
                const { channel } = response.data;
                if (channel === 'sms') {
                    toast.success('Login OTP sent to your mobile number');
                } else {
                    toast.success('Login OTP sent to your email');
                }
                navigate(`/verify-otp${window.location.search}`, { state: { email: formData.email, type: 'login', channel } });
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = async (credential) => {
        setLoading(true);
        try {
            const res = await googleLogin(credential, null, tenant?.id);
            
            if (res.requireRestaurantSelection) {
                setRestaurantAccounts(res.accounts);
                setGoogleCredential(credential);
                return;
            }

            const urlParams = new URLSearchParams(window.location.search);
            const returnOrigin = urlParams.get('return_origin');
            
            if (returnOrigin) {
                window.location.href = `${returnOrigin}/login?token=${res.token}`;
                return;
            }
            
            toast.success('Signed in with Google');
            navigateBasedOnRole(res.user, res.token);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Google login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-3xl shadow-2xl border border-gray-100 transform transition-all">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 tracking-tight">
                        {!showEmailForm ? 'Google Authentication' : (loginMode === 'password' ? 'Welcome Back' : 'OTP Sign In')}
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        {!showEmailForm 
                            ? 'Sign in securely with Google to continue' 
                            : (loginMode === 'password' 
                                ? 'Please enter your details to sign in' 
                                : 'We will send a one-time code to your email')}
                    </p>
                </div>
                {isPortal && (
                    <div className="flex p-1 bg-gray-100 rounded-xl mb-4 shadow-inner">
                        <button 
                            className="flex-1 py-2.5 text-xs font-black uppercase tracking-wider rounded-lg bg-primary-600 text-white shadow-sm"
                            disabled
                        >
                            Restaurant SaaS
                        </button>
                        <button 
                            type="button"
                            onClick={() => navigate('/sites/login')}
                            className="flex-1 py-2.5 text-xs font-black uppercase tracking-wider rounded-lg text-gray-500 hover:text-gray-900 transition-colors"
                        >
                            Website Builder
                        </button>
                    </div>
                )}

                {showEmailForm && (
                    <div className="flex p-1 bg-gray-100 rounded-xl mb-8">
                        <button 
                            onClick={() => setLoginMode('password')}
                            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${loginMode === 'password' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            Password
                        </button>
                        <button 
                            onClick={() => setLoginMode('otp')}
                            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${loginMode === 'otp' ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            OTP
                        </button>
                    </div>
                )}

                {restaurantAccounts ? (
                    <div className="space-y-4 animate-in fade-in zoom-in duration-300">
                        <h3 className="text-sm font-bold text-gray-900 border-b pb-2">Select Your Restaurant</h3>
                        <p className="text-xs text-gray-500">Your email is associated with multiple restaurants. Please choose one to proceed:</p>
                        <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar pr-2">
                            {restaurantAccounts.map((account) => (
                                <button
                                    key={`${account.restaurant_id}-${account.id || 'reg'}`}
                                    onClick={() => handleAccountSelect(account.restaurant_id, account.id)}
                                    className="w-full text-left p-4 bg-gray-50 hover:bg-primary-50 hover:border-primary-200 border border-gray-200 rounded-xl transition-all group"
                                >
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <p className="font-bold text-sm text-gray-900 group-hover:text-primary-700">
                                                {account.restaurant_name || account.franchise_name || 'Central Portal Admin'}
                                            </p>
                                            <p className="text-xs text-gray-500 capitalize">
                                                {account.isRegistrationOption ? 'Register New Customer Account' : account.role.replace('_', ' ')}
                                            </p>
                                        </div>
                                        <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => setRestaurantAccounts(null)}
                            className="w-full py-3 mt-2 text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                ) : !showEmailForm ? (
                    <div className="flex flex-col items-center justify-center py-6 space-y-6 animate-in fade-in duration-300">
                        <div className="text-center space-y-2">
                            <p className="text-xs text-gray-500 max-w-xs">
                                To secure your session, click the Google button below to sign in.
                            </p>
                        </div>

                        <div className="w-full flex justify-center py-2">
                            <GoogleLogin
                                onSuccess={credentialResponse => {
                                    handleGoogleSuccess(credentialResponse.credential);
                                }}
                                onError={() => {
                                    toast.error('Google login failed');
                                }}
                                shape="pill"
                                theme="filled_blue"
                                size="large"
                                width="280px"
                            />
                        </div>

                        <div className="pt-4 border-t border-gray-100 w-full text-center">
                            <button
                                type="button"
                                onClick={() => setShowEmailForm(true)}
                                className="text-xs font-bold text-primary-600 hover:text-primary-500 transition-colors"
                            >
                                Sign in with password instead
                            </button>
                        </div>
                    </div>
                ) : (
                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="rounded-md shadow-sm space-y-4">
                        <div className="relative">
                            <Mail className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                required
                                className="appearance-none relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-xl focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm transition-all"
                                placeholder="Email or Mobile number"
                                value={formData.email}
                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                            />
                        </div>
                        
                        {loginMode === 'password' && (
                            <div className="relative">
                                <Lock className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                <input
                                    type="password"
                                    required
                                    className="appearance-none relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-xl focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm transition-all"
                                    placeholder="Password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                                />
                            </div>
                        )}
                    </div>

                    {loginMode === 'password' && (
                        <div className="flex items-center justify-end">
                            <div className="text-sm">
                                <Link to="/forgot-password" weight="semibold" className="font-bold text-primary-600 hover:text-primary-500 transition-colors">
                                    Forgot your password?
                                </Link>
                            </div>
                        </div>
                    )}

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all shadow-lg shadow-primary-200"
                        >
                            {loading ? (
                                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                <>
                                    <span>{loginMode === 'password' ? 'Sign In' : 'Send OTP'}</span>
                                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                                </>
                            )}
                        </button>
                    </div>
                </form>
                )}

                {showEmailForm && (
                    <>
                        <div className="relative my-8">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-200"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500 font-medium">Or continue with</span>
                            </div>
                        </div>

                        <div className="flex justify-center">
                            {(!isPortal && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1' && !new URLSearchParams(window.location.search).get('return_origin')) ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const platformHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                                            ? 'http://localhost:5173'
                                            : 'https://www.aaharamcloud.com';
                                        const centralLoginUrl = `${platformHost}/login?return_origin=${encodeURIComponent(window.location.origin)}`;
                                        window.location.href = centralLoginUrl;
                                    }}
                                    className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-gray-300 rounded-full text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-all shadow-sm hover:border-gray-400"
                                >
                                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                                    </svg>
                                    <span>Sign in with Google</span>
                                </button>
                            ) : (
                                <GoogleLogin
                                    onSuccess={credentialResponse => {
                                        handleGoogleSuccess(credentialResponse.credential);
                                    }}
                                    onError={() => {
                                        toast.error('Google login failed');
                                    }}
                                    shape="pill"
                                    theme="outline"
                                    size="large"
                                />
                            )}
                        </div>
                    </>
                )}

                <div className="text-center mt-6 space-y-4">
                    <p className="text-sm text-gray-600">
                        Don't have an account?{' '}
                        <Link to="/register" className="font-bold text-primary-600 hover:text-primary-500 transition-colors">
                            Sign up for free
                        </Link>
                    </p>
                    
                    <div className="pt-4 border-t border-gray-50">
                        <Link to="/" className="text-xs font-bold text-gray-400 hover:text-primary-600 transition-all flex items-center justify-center gap-1 group">
                            <ArrowRight className="w-3 h-3 rotate-180 transition-transform group-hover:-translate-x-1" />
                            Back to {isPortal ? 'Aaharam Cloud' : 'Home'}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}